import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { PinPad } from '../app/PinPad'
import { hasWebGL } from '../app/webgl'
import { figureFor } from '../../shared/figures'
import { figureBg, FigureIcon } from '../art/FigureIcon'
import { HttpError, Offline } from '../data/api'
import { authError, useSession } from '../data/session'
import { isSolo } from '../domain/players'
import { useMotionTier } from '../motion/tier'
import { fmt, t } from '../strings/en'
import { useMachineWords } from '../arcade/machines'
import { Welcome } from './Welcome'

const WhoStage = lazy(() => import('./WhoStage'))

/** Long enough to see the picked figure jump before the app opens. */
const CHEER_MS = 750


/** The words for a refused PIN, or null if this wasn't a PIN problem. */
export function pinProblem(e: unknown): string | null {
  const why = authError(e)
  if (why?.error === 'wrong pin') return t.pin.wrong(why.triesLeft)
  if (why?.error === 'locked') return t.pin.locked(new Date(why.until).toTimeString().slice(0, 5))
  return null
}

/**
 * The Who screen: the gate before anyone is signed in. (Settings › Switch, once someone is, is a
 * sheet over the app: screens/SwitchSheet, WI-17.)
 */
export function WhoScreen({ pack }: { pack: Pack | null }) {
  const words = useMachineWords()
  const { people, loadPeople, choose, linkState, resident, person: me } = useSession()
  const [pinFor, setPinFor] = useState<Person | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const tier = useMotionTier()
  const [webgl, setWebgl] = useState(hasWebGL)
  // No 3D until the phone is known to be in a family (WI-22, review L0-06): a phone on its way to
  // Welcome never loads the 3D stack.
  const globe = webgl && linkState === 'linked'
  const [hover, setHover] = useState<string | null>(null)
  const [cheer, setCheer] = useState<string | null>(null)
  const list = people ?? []
  const looks = new Map(list.map((p) => [p.id, figureFor(p, list)]))
  const members = list.map((p) => ({ id: p.id, kind: p.kind, figure: looks.get(p.id)! }))
  // Up to two people get the big cards; a bigger family gets a two-column grid (mockup o01).
  const grid = list.length > 2
  // Whose phone this is (WI-17): a grown-up picked on a child's phone is in parent mode.
  const residentChild = list.find((p) => p.kind === 'child' && p.id === (resident ?? (me?.kind === 'child' ? me.id : null)))
  const parentModeFor = (p: Person) => (p.kind !== 'child' && residentChild ? residentChild : null)

  useEffect(() => {
    loadPeople().catch(() => setError(t.who.offline))
  }, [loadPeople])

  const signIn = (id: string, pin?: string) => choose(id, pin)

  const pick = async (id: string) => {
    if (people?.find((p) => p.id === id)?.pin) {
      setPinFor(people.find((p) => p.id === id) ?? null)
      return
    }
    setBusy(id)
    setError(null)
    if (globe) {
      setCheer(id)
      await new Promise((r) => setTimeout(r, CHEER_MS))
    }
    try {
      await signIn(id)
    } catch (e) {
      // Someone removed (or re-added) since this list was cached: refresh it.
      if (e instanceof HttpError && e.status === 404) void loadPeople().catch(() => undefined)
      setError(
        e instanceof Offline
          ? t.who.offline
          : e instanceof HttpError && e.status === 404
            ? t.who.gone
            : authError(e)?.error === 'set a PIN first'
              ? t.who.setPinFirst
              : String(e),
      )
    } finally {
      setBusy(null)
      setCheer(null)
    }
  }

  // Just me (WI-37): there's nobody else to pick, so go straight to the one person (their PIN, if
  // they have one). Once only: cancelling the PIN shows the picker rather than looping.
  const autoPicked = useRef(false)
  const solo = isSolo(people)
  useEffect(() => {
    if (!solo || linkState === 'needs-link' || autoPicked.current || !people?.[0]) return
    autoPicked.current = true
    void pick(people[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the list first says "just me"
  }, [solo, linkState])

  if (linkState === 'needs-link') return <Welcome />

  if (pinFor) {
    // The PIN pad says what this pick means: "Dad — parent mode for 30 min on Lyam's phone".
    const child = parentModeFor(pinFor)
    return (
      <main className="screen who">
        <PinPad
          title={t.pin.hi(pinFor.name)}
          sub={child ? t.who.parentMode(pinFor.name, child.name) : t.pin.enter}
          head={
            <span className="who-pin-av" style={{ background: figureBg(looks.get(pinFor.id) ?? figureFor(pinFor)) }}>
              <FigureIcon figure={looks.get(pinFor.id) ?? figureFor(pinFor)} size={60} />
            </span>
          }
          onCancel={() => setPinFor(null)}
          onSubmit={async (pin) => {
            try {
              await signIn(pinFor.id, pin)
              return null
            } catch (e) {
              return pinProblem(e) ?? (e instanceof Offline ? t.who.offline : String(e))
            }
          }}
        />
      </main>
    )
  }

  return (
    <main className={`screen who${globe ? ' has-globe' : ''}`}>
      {pack ? (
        <span className="pill">
          {pack.meta.title} · {fmt.tripDates(pack.meta.startDate, pack.meta.endDate)}
        </span>
      ) : null}
      {globe ? (
        <Suspense fallback={<div className="who-globe" />}>
          <WhoStage
            // A new canvas when motion changes: a released GL context can't be reused.
            key={tier === 'full' ? 'live' : 'still'}
            animate={tier === 'full'}
            members={members}
            hover={hover}
            cheer={cheer}
            onFail={() => setWebgl(false)}
            onPick={(id) => {
              if (!busy) void pick(id)
            }}
          />
        </Suspense>
      ) : null}
      <h1 className="h1 who-title">{t.who.title}</h1>
      <p className="muted">{t.who.sub}</p>
      <div className={`who-list${grid ? ' grid' : ''}`}>
        {list.map((p) => {
          const look = looks.get(p.id)!
          const grown = p.kind !== 'child'
          const child = parentModeFor(p)
          return (
            <button key={p.id} className="panel who-tile press" onClick={() => void pick(p.id)}
              onPointerEnter={() => setHover(p.id)}
              onPointerDown={() => setHover(p.id)}
              onPointerLeave={() => setHover(null)}
              onFocus={() => setHover(p.id)}
              onBlur={() => setHover(null)}
              disabled={!!busy}
              aria-busy={busy === p.id}
            >
              <span className="av" style={{ background: figureBg(look) }}>
                <FigureIcon figure={look} size={grid ? 40 : 64} />
              </span>
              <span className="who-text">
                <b>{p.name}</b>
                {grid ? null : <span className={`tag ${grown ? 'mint' : 'blue'}`}>{grown ? t.who.organiser : t.who.player}</span>}
                <small>{child ? t.who.parentModeLine(child.name) : p.pin ? t.who.parentLine : grown ? (grid ? t.who.organiser.toLowerCase() : t.who.organiserLine) : grid ? t.who.player.toLowerCase() : t.who.playerLine(words)}</small>
              </span>
            </button>
          )
        })}
      </div>
      {error ? <p className="who-error" role="alert">{error}</p> : null}
      <p className="who-foot">{t.who.needsSignal}</p>
    </main>
  )
}
